import { createContext, ReactNode, useCallback, useState } from 'react';
import { usePageContext } from './hooks/usePageContext';
import axiosInstance from 'utils/axiosInstance';
import { ITransactionInitiationPayload } from 'types';
import { AxiosResponse } from 'axios';
import { PAGE_SUB, PAGES } from './pageContext';
import { PAYMENT_PROVIDER } from 'types/enums';
import { isCustomsProvider, selectInitialProvider } from 'config/providers';
import { IFeeQuote, PaymentMethod } from 'utils/feeQuote';

export interface IInitiateTransactionPayload {
  customerEmail: string;
  customerPhone: string;
  amount: number;
  invoiceId?: string;
  currency: string;
  provider?: PAYMENT_PROVIDER;
  customerId: string;
  customerRef?: string;
  /** The merchant's metadata and any customs fields, kept on the collection transaction. */
  metadata?: Record<string, string>;
  /** A customs assessment payment, which goes through FirstChekout when it is offered. */
  isCustomsPayment?: boolean;
}

export interface IProviderTransaction {
  ceviantTransactionId: string;
  subAccountId: string;
  providerPublicKey: string;
  merchantName: string;
  merchantLogo: string | null;
  merchantCollectionPublicKey: string;
  provider: string;
}

type TransactionContextType = {
  ceviantTransactionId: string;
  merchantLogo: string | null;
  merchantName: string;
  providerPublicKey: string;
  merchantCollectionPublicKey: string;
  subAccountId: string;
  transactions: IProviderTransaction[];
  feeQuotes: IFeeQuote[];
  /** the payment method the customer is currently on, remembered so the success screen can show what they paid */
  paymentMethod?: PaymentMethod;
  changeTransactionDetails: (details: Partial<TransactionContextType>) => void;
  initiateTransaction: (data: IInitiateTransactionPayload) => void;
  selectProvider: (providerName: PAYMENT_PROVIDER) => void;
  customerId: string;
  customerEmail: string;
  customerPhone: string;
  publicKey: string;
  amount: number;
  currency: string;
  invoiceId?: string;
  provider?: string;
  customerRef?: string;
  /** The provider's own reference, for providers that complete by it. */
  providerTransactionRef: string;
  /** Kept so a retry starts the transaction with the same metadata. */
  metadata?: Record<string, string>;
  isCustomsPayment?: boolean;
};

export const TransactionContext = createContext<TransactionContextType>({
  ceviantTransactionId: '',
  merchantLogo: null,
  merchantName: '',
  providerPublicKey: '',
  merchantCollectionPublicKey: '',
  subAccountId: '',
  transactions: [],
  feeQuotes: [],
  changeTransactionDetails: () => {},
  initiateTransaction: () => {},
  selectProvider: () => {},
  customerId: '',
  customerEmail: '',
  customerPhone: '',
  publicKey: '',
  amount: 0,
  currency: '',
  invoiceId: '',
  provider: '',
  customerRef: '',
  providerTransactionRef: '',
});

const TransactionProvider = ({ children }: { children: ReactNode }) => {
  const { changePage, changeSub } = usePageContext();
  const [transactionDetails, setTransactionDetails] =
    useState<TransactionContextType>({
      ceviantTransactionId: '',
      merchantLogo: null,
      merchantName: '',
      providerPublicKey: '',
      merchantCollectionPublicKey: '',
      subAccountId: '',
      transactions: [],
      feeQuotes: [],
      changeTransactionDetails: () => {},
      initiateTransaction: () => {},
          selectProvider: () => {},
      customerId: '',
      customerEmail: '',
      customerPhone: '',
      publicKey: '',
      amount: 0,
      currency: '',
      invoiceId: '',
      provider: '',
      customerRef: '',
      providerTransactionRef: '',
    });

  const changeTransactionDetails = useCallback(
    (details: Partial<TransactionContextType>) => {
      setTransactionDetails((prevDetails) => ({
        ...prevDetails,
        ...details,
      }));
    },
    [],
  );

  const selectProvider = useCallback((providerName: PAYMENT_PROVIDER) => {
    setTransactionDetails((prevDetails) => {
      const selectedTransaction = prevDetails.transactions.find(
        (transaction) => transaction.provider === providerName,
      );

      if (!selectedTransaction) {
        console.error(`Provider ${providerName} not found in transactions`);
        return prevDetails;
      }

      return {
        ...prevDetails,
        ceviantTransactionId: selectedTransaction.ceviantTransactionId,
        providerPublicKey: selectedTransaction.providerPublicKey,
        merchantCollectionPublicKey:
          selectedTransaction.merchantCollectionPublicKey,
        subAccountId: selectedTransaction.subAccountId,
        merchantName: selectedTransaction.merchantName,
        merchantLogo: selectedTransaction.merchantLogo,
        provider: selectedTransaction.provider,
      };
    });
  }, []);

  const requestProviders = async (data: IInitiateTransactionPayload) => {
    const {
      customerEmail,
      customerPhone,
      amount,
      invoiceId,
      currency,
      customerId,
      customerRef,
      metadata,
      isCustomsPayment,
    } = data;

    const response = await axiosInstance.post<
      ITransactionInitiationPayload,
      AxiosResponse<{
        transactions: IProviderTransaction[];
        feeQuotes?: IFeeQuote[];
      }>
    >('/transactions/allProviders', {
      customerEmail,
      customerPhone,
      amount,
      invoiceId,
      currency,
      customerId,
      customerReference: customerRef || customerId,
      metadata,
    });

    if (!response.data?.transactions) {
      return null;
    }

    const { transactions } = response.data;

    changeTransactionDetails({
      transactions,
      feeQuotes: response.data.feeQuotes ?? [],
      customerEmail,
      customerPhone,
      amount,
      currency,
      customerId,
      invoiceId,
      metadata,
      isCustomsPayment,
    });

    const initialProvider = selectInitialProvider(
      transactions.map((t) => t.provider),
      !!isCustomsPayment,
    );

    if (isCustomsPayment && !isCustomsProvider(initialProvider)) {
      // Deliberate: the payment proceeds as an ordinary one. The assessment
      // was already sent as metadata with allProviders, so it is kept on the
      // transaction. Logged so these cases can be found later.
      console.warn(
        'FIRSTCHEKOUT unavailable; customs payment falling back to',
        initialProvider,
        metadata,
      );
    }

    selectProvider(initialProvider);
    changePage(PAGES.CARD);

    return transactions;
  };

  const initiateTransaction = async (data: IInitiateTransactionPayload) => {
    try {
      await requestProviders(data);
    } catch (error) {
      changeSub(PAGE_SUB.ERROR);
    }
  };

  return (
    <TransactionContext.Provider
      value={{
        ...transactionDetails,
        changeTransactionDetails,
        initiateTransaction,
        selectProvider,
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};

export default TransactionProvider;
